import ConstructionIcon from '@mui/icons-material/Construction';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { useBatchEdit } from 'components/BatchEdit/context';
import { useExportImport } from 'components/ExportImport/ExportImportContext';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { Fragment, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { bloxValues } from 'themes/constants';
import {
  checkBatchEditSearchPermission,
  checkSiteAdminPermission,
  PermissionInfo,
} from 'utility/permissions';
import { useSearchItems } from 'components/Search/components/SearchItemsProvider';

export const EditorialEndSearchContent = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { toggleDialog: toggleDialogBatch } = useBatchEdit();
  const {
    export: { handleExportSubmit },
  } = useExportImport();
  const { selectedItemIds } = useSearchItems();
  const selectedExportIds = useMemo(
    () => Array.from(selectedItemIds.ids).map((id) => String(id)),
    [selectedItemIds],
  );
  const selectedItemCount = selectedExportIds.length;
  const hasSelectedItems = Boolean(selectedItemCount > 0);
  const { sessionConfig } = useCosmos();
  const { granted: canBatchEditSearch, name: batchPermName } =
    checkBatchEditSearchPermission(sessionConfig);
  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExport = useCallback(
    async (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      setSubmitting(true);
      const success = await handleExportSubmit(event, selectedExportIds);
      if (success) {
        handleClose();
      }
      setSubmitting(false);
    },
    [handleExportSubmit, selectedExportIds],
  );

  const btnBatchState: PermissionInfo = useMemo(() => {
    if (userIsAdmin) {
      return {
        info: t('blox.batch_edit.label'),
        disabled: false,
      };
    }

    if (!canBatchEditSearch) {
      return {
        info: t('blox.permissions.msg.required_short', { name: batchPermName }),
        disabled: true,
      };
    }

    return {
      info: t('blox.batch_edit.label'),
      disabled: false,
    };
  }, [t, canBatchEditSearch, batchPermName, userIsAdmin]);

  const btnExportState: PermissionInfo = useMemo(() => {
    return {
      info: t('blox.export.label'),
      disabled: !hasSelectedItems,
    };
  }, [t, hasSelectedItems]);

  return (
    <Fragment>
      <Tooltip
        title={`${t('blox.editorial.toolbar.open_more_options')}`}
        placement="bottom"
      >
        <span>
          <IconButton
            data-testid="editorial-search-options-menu"
            aria-controls={open ? 'editorial-search-options-menu' : undefined}
            aria-label="search options menu"
            aria-describedby="editorial-search-options-menu"
            size="small"
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            sx={{ p: isMobile ? 0 : undefined }}
          >
            <MoreVertIcon
              sx={{
                height: bloxValues.iconSizeRegular,
                color: 'inherit',
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        id="editorial-search-options-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': 'editorial-search-options-menu',
          },
        }}
      >
        <MenuItem
          data-testid="editorial-search-export-results"
          aria-label="export search results"
          disabled={submitting || !hasSelectedItems || btnExportState.disabled}
          onClick={handleExport}
        >
          <ListItemIcon sx={{ position: 'relative' }}>
            <DownloadIcon
              sx={{
                height: bloxValues.iconSizeRegular,
                width: bloxValues.iconSizeRegular,
                color: 'inherit',
              }}
            />
            {submitting && (
              <CircularProgress
                size={28}
                sx={{
                  position: 'absolute',
                  top: -2,
                  left: -2,
                }}
              />
            )}
          </ListItemIcon>
          <ListItemText
            sx={{
              width: '100%',
              '& .MuiTypography-root': {
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              },
            }}
          >
            {btnExportState.info}
          </ListItemText>
        </MenuItem>
        <MenuItem
          data-testid="editorial-search-batch-edit-results"
          aria-label="batch edit search results"
          disabled={btnBatchState.disabled}
          onClick={() => toggleDialogBatch(true)}
        >
          <ListItemIcon>
            <ConstructionIcon
              sx={{
                height: bloxValues.iconSizeRegular,
                color: 'inherit',
              }}
            />
          </ListItemIcon>
          <ListItemText>{btnBatchState.info}</ListItemText>
        </MenuItem>
      </Menu>
    </Fragment>
  );
};
